import PageHeader from "../core/App/PageHeader";
import PagePanel from "../core/App/PagePanel";
import { constantToDisplayName } from "../core/constantToDisplayName";
import { AccessType, useMeQuery, UserRole } from 'legoland-sdk/dist/experimental';
import moment from 'moment';
import { useEffect, useState } from 'react';
import ReportsExcludeRecords from "./ReportsExcludeRecords";
import { Box, Button, FormGroup, Heading, HGrid, Input, RadioGroup, Select, useToasts, VGrid } from 'tombac';
import { formatDate } from "./reportsClient";
import { useDeleteProcessingReport, useDownloadReportQuery, useReportStatusQuery, useRequestReportMutation } from 'app/modules/reports/reportsApiHooks';
import { ReportStatusType } from 'app/modules/reports/reportTypes';
import { AppDatePicker } from "../core/AppDatePicker";
import OrganizationPicker from "../core/components/OrganizationPicker/OrganizationPicker";
import saveAs from 'file-saver';
import ReportDownloadInfo from "./ReportDownload";
import "./ReportsPage.css";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const reportTypeOptions = [{
  label: 'Usage',
  value: 'Usage'
}, {
  label: 'Finance',
  value: 'Finance'
}];
const reportFormatOptions = [{
  label: 'CSV',
  value: 'Csv'
}, {
  label: 'XLS',
  value: 'Xls'
}];
const productOptions = [{
  label: 'O/D Analysis',
  value: 'od'
}, {
  label: 'Traffic Stats',
  value: 'ts'
}, {
  label: 'Route Monitoring',
  value: 'ctm'
}, {
  label: 'Junction Analytics',
  value: 'ja'
}, {
  label: 'Area Analytics',
  value: 'aa'
}];
const validateRange = (start, end) => {
  const MAX_DURATION_DAYS = 365;
  const startMoment = moment(start);
  const endMoment = moment(end);
  const rangeDurationDays = Math.floor(moment.duration(endMoment.diff(startMoment)).asDays());
  if (rangeDurationDays < 0) {
    return {
      valid: false,
      reason: 'Start date is ahead of end date'
    };
  }
  if (rangeDurationDays > MAX_DURATION_DAYS) {
    return {
      valid: false,
      reason: `Date range is too big. Selected ${rangeDurationDays} when maximum allowed duration is ${MAX_DURATION_DAYS} days.`
    };
  }
  return {
    valid: true
  };
};
function ReportsPage() {
  const meQuery = useMeQuery();
  const role = meQuery.data.me.role;
  const {
    addToast
  } = useToasts();
  const [rangeStart, setRangeStart] = useState(moment(new Date()).subtract(1, 'week').toDate());
  const [rangeEnd, setRangeEnd] = useState(new Date());
  const [productId, setProductId] = useState('od');
  const [accessType, setAccessType] = useState('');
  const [excludeEmailPattern, setExcludeEmailPattern] = useState('');
  const [includeEmailPattern, setIncludeEmailPattern] = useState('');
  const [sapId, setSapId] = useState('');
  const [organizationId, setOganizationId] = useState(null);
  const [reportStatusInterval, setReportStatusInterval] = useState(null);
  const {
    data: reportStatus
  } = useReportStatusQuery(undefined, {
    refetchInterval: reportStatusInterval
  });
  const {
    mutate: requestReport
  } = useRequestReportMutation();
  const {
    mutate: deleteProcessingReport
  } = useDeleteProcessingReport();
  const downloadReport = useDownloadReportQuery({
    enabled: false,
    onSuccess: data => saveReportFile(data)
  });
  const saveReportFile = async reportFile => {
    const {
      request: reportData
    } = reportStatus;
    const blob = await reportFile.blob();
    const filename = `${constantToDisplayName(reportData.type)} Report ${reportData.productId.toUpperCase()} ${reportData.from} to ${reportData.to}`;
    return saveAs(blob, filename);
  };
  useEffect(() => {
    const status = reportStatus?.status;
    if (status) {
      if (status === ReportStatusType.Error || status === ReportStatusType.Done) {
        setReportStatusInterval(null);
      }
      if (status === ReportStatusType.InProgress) {
        setReportStatusInterval(3000);
      }
    } else {
      setReportStatusInterval(null);
    }
  }, [reportStatus?.status]);
  const handleSubmit = () => {
    const reportParams = {
      reportType: reportType.value.toUpperCase(),
      reportFormat: reportFormat.label === 'XLS' ? 'EXCEL' : 'CSV',
      productId,
      rangeStart: formatDate(rangeStart),
      rangeEnd: formatDate(rangeEnd),
      sapIds: sapId ? sapId.split(',').map(id => id.trim()).filter(id => id !== '') : [],
      organizationIds: organizationId ? [organizationId] : [],
      ...(accessType && {
        accessType
      }),
      ...(excludeEmailPattern && {
        excludeEmailPattern
      }),
      ...(includeEmailPattern && {
        includeEmailPattern
      })
    };
    requestReport(reportParams, {
      onError: async error => {
        console.error(error);
        const response = await error.json();
        addToast(response.message ? /*#__PURE__*/_jsx("div", {
          style: {
            overflowWrap: 'anywhere',
            paddingRight: '5px'
          },
          children: response.message
        }) : 'Failed to request for the report', 'danger');
      }
    });
  };
  const rangeValidStatus = validateRange(rangeStart, rangeEnd);
  const [accessTypeOptions, setAccessTypeOptions] = useState([]);
  useEffect(function updateAccessTypeOptions() {
    const accessTypes = role === UserRole.User ? [] : role === UserRole.Distributor ? [AccessType.Paying, AccessType.Evaluation] : Object.keys(AccessType).map(key => AccessType[key]);
    setAccessTypeOptions([{
      label: 'Any',
      value: ''
    }, ...accessTypes.map(type => ({
      label: constantToDisplayName(type),
      value: type
    }))]);
  }, [role]);
  const [reportType, setReportType] = useState(reportTypeOptions[0]);
  const [reportFormat, setReportFormat] = useState(reportFormatOptions[1]);
  const reportFormatDisabled = reportType.value === 'Finance';
  const canChooseReportType = role === UserRole.Admin || role === UserRole.Support;
  const onReportTypeChange = reportType => {
    if (reportType.value === 'Finance') setReportFormat(reportFormatOptions[1]);
    setReportType(reportType);
  };
  const canGenerateReport = reportStatus?.status !== ReportStatusType.InProgress && rangeValidStatus.valid;
  return /*#__PURE__*/_jsxs("div", {
    "data-sentry-component": "ReportsPage",
    "data-sentry-source-file": "ReportsPage.tsx",
    children: [/*#__PURE__*/_jsx(PageHeader, {
      transparent: true,
      "data-sentry-element": "PageHeader",
      "data-sentry-source-file": "ReportsPage.tsx",
      "data-sentry-element": "PageHeader",
      children: /*#__PURE__*/_jsx(Heading, {
        level: 3,
        $mb: "0sp",
        "data-sentry-element": "Heading",
        "data-sentry-source-file": "ReportsPage.tsx",
        "data-sentry-element": "Heading",
        children: "Reports"
      })
    }), /*#__PURE__*/_jsx(PagePanel, {
      $padding: "60u",
      $borderRadius: "20u",
      "data-sentry-element": "PagePanel",
      "data-sentry-source-file": "ReportsPage.tsx",
      "data-sentry-element": "PagePanel",
      children: /*#__PURE__*/_jsxs(Box, {
        className: "ReportsPage__content",
        $width: "min-content",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "ReportsPage.tsx",
        "data-sentry-element": "Box",
        children: [rangeValidStatus.valid === false && /*#__PURE__*/_jsx("div", {
          className: "ReportsPage__error",
          children: rangeValidStatus.reason
        }), /*#__PURE__*/_jsxs(Box, {
          className: "ReportsPage__form",
          $display: "grid",
          $gap: "5sp 10sp",
          $gridAutoColumns: "540u",
          $width: "max-content",
          "data-sentry-element": "Box",
          "data-sentry-source-file": "ReportsPage.tsx",
          "data-sentry-element": "Box",
          children: [/*#__PURE__*/_jsxs(VGrid, {
            gap: 3,
            justifyContent: "stretch",
            "data-sentry-element": "VGrid",
            "data-sentry-source-file": "ReportsPage.tsx",
            "data-sentry-element": "VGrid",
            children: [/*#__PURE__*/_jsx(Heading, {
              as: "h2",
              level: 3,
              $mb: "0sp",
              "data-sentry-element": "Heading",
              "data-sentry-source-file": "ReportsPage.tsx",
              "data-sentry-element": "Heading",
              children: canChooseReportType ? 'Report' : 'Usage report'
            }), /*#__PURE__*/_jsx(FormGroup, {
              htmlFor: "report-report-type-input",
              label: canChooseReportType ? 'Type' : 'Format',
              "data-sentry-element": "FormGroup",
              "data-sentry-source-file": "ReportsPage.tsx",
              "data-sentry-element": "FormGroup",
              children: /*#__PURE__*/_jsxs(HGrid, {
                gridTemplateColumns: "1fr",
                "data-sentry-element": "HGrid",
                "data-sentry-source-file": "ReportsPage.tsx",
                "data-sentry-element": "HGrid",
                children: [canChooseReportType && /*#__PURE__*/_jsx(RadioGroup, {
                  onChange: onReportTypeChange,
                  options: reportTypeOptions,
                  variant: "horizontal",
                  value: reportType
                }), /*#__PURE__*/_jsx(RadioGroup, {
                  disabled: reportFormatDisabled,
                  onChange: setReportFormat,
                  options: reportFormatOptions,
                  variant: "horizontal",
                  value: reportFormat,
                  "data-sentry-element": "RadioGroup",
                  "data-sentry-source-file": "ReportsPage.tsx",
                  "data-sentry-element": "RadioGroup"
                })]
              })
            }), /*#__PURE__*/_jsx(FormGroup, {
              htmlFor: "report-products-input",
              label: "Products",
              "data-sentry-element": "FormGroup",
              "data-sentry-source-file": "ReportsPage.tsx",
              "data-sentry-element": "FormGroup",
              children: /*#__PURE__*/_jsx(Select, {
                id: "report-products-input",
                onChange: o => !('length' in o) && setProductId(o.value),
                options: productOptions,
                value: productOptions.find(o => o.value === productId),
                "data-sentry-element": "Select",
                "data-sentry-source-file": "ReportsPage.tsx",
                "data-sentry-element": "Select"
              })
            }), /*#__PURE__*/_jsxs(HGrid, {
              gridAutoColumns: "1fr",
              "data-sentry-element": "HGrid",
              "data-sentry-source-file": "ReportsPage.tsx",
              "data-sentry-element": "HGrid",
              children: [/*#__PURE__*/_jsx(FormGroup, {
                htmlFor: "report-from-input",
                label: "From",
                "data-sentry-element": "FormGroup",
                "data-sentry-source-file": "ReportsPage.tsx",
                "data-sentry-element": "FormGroup",
                children: /*#__PURE__*/_jsx(AppDatePicker, {
                  id: "report-from-input",
                  onChange: value => {
                    setRangeStart(value);
                  },
                  placeholder: "Select start date",
                  value: rangeStart,
                  "data-sentry-element": "AppDatePicker",
                  "data-sentry-source-file": "ReportsPage.tsx",
                  "data-sentry-element": "AppDatePicker"
                })
              }), /*#__PURE__*/_jsx(FormGroup, {
                htmlFor: "report-to-input",
                label: "To",
                "data-sentry-element": "FormGroup",
                "data-sentry-source-file": "ReportsPage.tsx",
                "data-sentry-element": "FormGroup",
                children: /*#__PURE__*/_jsx(AppDatePicker, {
                  id: "report-to-input",
                  onChange: value => {
                    setRangeEnd(value);
                  },
                  placeholder: "Select end date",
                  value: rangeEnd,
                  "data-sentry-element": "AppDatePicker",
                  "data-sentry-source-file": "ReportsPage.tsx",
                  "data-sentry-element": "AppDatePicker"
                })
              })]
            })]
          }), role !== UserRole.User && /*#__PURE__*/_jsxs(VGrid, {
            gap: 3,
            gridAutoColumns: "1fr",
            children: [/*#__PURE__*/_jsx(Heading, {
              as: "h2",
              level: 3,
              $mb: "0sp",
              children: "Users"
            }), accessTypeOptions.length > 1 && /*#__PURE__*/_jsx(FormGroup, {
              htmlFor: "report-access-type-input",
              label: "Access type",
              children: /*#__PURE__*/_jsx(Select, {
                onChange: accessType => !('length' in accessType) && setAccessType(accessType.value),
                options: accessTypeOptions,
                value: accessTypeOptions.find(o => o.value === accessType)
              })
            }), (role === UserRole.Admin || role === UserRole.Support) && /*#__PURE__*/_jsx(FormGroup, {
              htmlFor: "report-sapid-input",
              label: "Filter by SAP IDs",
              children: /*#__PURE__*/_jsx(Input, {
                id: "report-sapid-input",
                onChange: e => setSapId(e.target.value),
                value: sapId
              })
            }), /*#__PURE__*/_jsx(OrganizationPicker, {
              allowCreate: false,
              label: 'Filter by organization',
              onChange: setOganizationId,
              value: organizationId
            }), /*#__PURE__*/_jsx(FormGroup, {
              htmlFor: "report-exclude-input",
              label: "Exclude emails containing",
              children: /*#__PURE__*/_jsx(Input, {
                id: "report-exclude-input",
                onChange: e => setExcludeEmailPattern(e.target.value),
                value: excludeEmailPattern
              })
            }), /*#__PURE__*/_jsx(FormGroup, {
              htmlFor: "report-include-input",
              label: "Only include emails containing",
              children: /*#__PURE__*/_jsx(Input, {
                id: "report-include-input",
                onChange: e => setIncludeEmailPattern(e.target.value),
                value: includeEmailPattern
              })
            })]
          }), /*#__PURE__*/_jsx(Box, {
            $display: "flex",
            $gridColumn: role === UserRole.User ? '1' : '2',
            $justifyContent: "flex-end",
            "data-sentry-element": "Box",
            "data-sentry-source-file": "ReportsPage.tsx",
            "data-sentry-element": "Box",
            children: /*#__PURE__*/_jsx(Button, {
              onClick: handleSubmit,
              disabled: !canGenerateReport,
              variant: "primary",
              "data-sentry-element": "Button",
              "data-sentry-source-file": "ReportsPage.tsx",
              "data-sentry-element": "Button",
              children: "Generate report"
            })
          }), /*#__PURE__*/_jsx(Box, {
            $display: "flex",
            $gridColumn: role === UserRole.User ? '1' : '2',
            $justifyContent: "space-between",
            "data-sentry-element": "Box",
            "data-sentry-source-file": "ReportsPage.tsx",
            "data-sentry-element": "Box",
            children: reportStatus && /*#__PURE__*/_jsx(ReportDownloadInfo, {
              reportStatusInfo: reportStatus,
              onDownload: () => {
                downloadReport.refetch();
              },
              onCancel: () => {
                deleteProcessingReport(undefined);
              }
            })
          })]
        })]
      })
    }), role === UserRole.Admin && /*#__PURE__*/_jsx(PagePanel, {
      $width: "100%",
      breakpoint: 1000,
      $p: "60u",
      $borderRadius: "20u",
      children: /*#__PURE__*/_jsx(Box, {
        className: "ReportsPage__content",
        $display: "grid",
        $gap: "5sp 10sp",
        $gridAutoColumns: "1fr",
        children: /*#__PURE__*/_jsxs(VGrid, {
          gap: 3,
          gridAutoColumns: "1fr",
          children: [/*#__PURE__*/_jsx(Heading, {
            as: "h2",
            level: 3,
            $mb: "0sp",
            children: "Finance report exclusions"
          }), /*#__PURE__*/_jsx(ReportsExcludeRecords, {
            productOptions: productOptions
          })]
        })
      })
    })]
  });
}
export default ReportsPage;